/**
 * product-card-metals.js — Volet « tous les métaux » des cartes produit
 * -------------------------------------------------------------------------
 * Sur les cartes produit (.product-card), seules les 3 premières pastilles
 * métal disponibles sont affichées (les suivantes portent .swatch-items--extra,
 * masquées en CSS). Un chevron (.metals-toggle) ouvre au survol un volet
 * (.product-card__metals-drawer) qui monte du bas de la carte et présente
 * toutes les pastilles ; il redescend quand le pointeur quitte la zone
 * chevron + volet. Le chevron bascule ▲/▼ via la classe .metals-open posée
 * sur la carte (états pilotés par classe, jamais par :hover — compatible
 * tactile et clavier).
 *
 * Le volet est un simple miroir : chaque clic sur une pastille du volet est
 * relayé (clic natif) vers la pastille d'origine de la rangée, dont le
 * gestionnaire direct jQuery (wpbingo.click_atribute_image) fait autorité
 * (classe active, prix, disponibilité). Ces gestionnaires sont re-liés après
 * chaque rendu ajax (facets.js, wishlist, produits récents) — le relais
 * trouve donc toujours un gestionnaire vivant.
 *
 * Le module recale aussi les infobulles (nom du métal) : la carte ne clippe plus
 * qu'à la verticale (css-site.css.liquid, .product-card { overflow: visible clip }),
 * mais le .slick-list des carrousels et le bord de page, eux, clippent encore.
 *
 * Tout est délégué sur document : aucune initialisation par carte, le module
 * survit aux re-rendus ajax, aux clones slick et à l'éditeur de thème.
 */
(function () {
  'use strict';

  var OPEN = 'metals-open';
  var CLOSE_DELAY = 120; // ms — absorbe le trajet du pointeur chevron → volet
  var timers = new WeakMap(); // carte → id du timer de fermeture
  var proxying = false; // vrai pendant le clic relayé volet → rangée
  var TIP_MARGIN = 4; // px de respiration entre l'infobulle et le bord clippant
  // Porteurs d'une infobulle, cherchés dans une carte : pastille de la rangée
  // et pastille du volet.
  var TIP_ANCHORS = '.wpb-variants-swatch .swatch-items label, .metals-drawer__item';

  function card(el) {
    return el && el.closest ? el.closest('.product-card') : null;
  }
  // Chevron de la rangée (celui du volet est décoratif/fermeture).
  function rowToggleOf(c) {
    return c.querySelector('.wpb-variants-swatch .metals-toggle');
  }
  function setExpanded(c, expanded) {
    c.querySelectorAll('.metals-toggle').forEach(function (t) {
      t.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  // La « zone » = chevron + volet de la même carte.
  function inZone(el, c) {
    if (!el || !c || !el.closest || !c.contains(el)) return false;
    return !!el.closest('.metals-toggle, [data-metals-drawer]');
  }

  function cancelClose(c) {
    var t = timers.get(c);
    if (t) {
      clearTimeout(t);
      timers.delete(c);
    }
  }

  // Recopie l'état actif/désactivé depuis la rangée d'origine (source de
  // vérité tenue par wpbingo) vers les pastilles du volet.
  function sync(c) {
    var state = {};
    var extraActive = false;
    c.querySelectorAll('.wpb-variants-swatch .swatch-content.metal-swatch .swatch-items').forEach(function (si) {
      var lb = si.querySelector('label[data-variant]');
      if (!lb) return;
      var active = si.classList.contains('active');
      if (active && si.classList.contains('swatch-items--extra')) extraActive = true;
      state[lb.getAttribute('data-variant')] = {
        active: active,
        disabled: si.classList.contains('disabled')
      };
    });
    c.querySelectorAll('.metals-drawer__item').forEach(function (di) {
      var st = state[di.getAttribute('data-variant')];
      if (!st) return;
      di.classList.toggle('is-active', st.active);
      di.classList.toggle('is-disabled', st.disabled);
    });
    // Un métal « extra » sélectionné s'affiche en 4e pastille : on masque le
    // titre de la rangée (CSS) pour que la ligne ne se replie pas et que la
    // hauteur de la carte — donc la position du volet — ne bouge pas.
    c.classList.toggle('metals-extra-active', extraActive);
    // La rangée vient peut-être de changer (4e pastille, titre masqué) : les
    // bulles ont bougé, leur recalage est à refaire.
    placeTips(c);
  }

  function open(c) {
    if (!c) return;
    cancelClose(c);
    if (c.classList.contains(OPEN)) return;
    // Une seule carte ouverte à la fois (surtout utile au tactile).
    document.querySelectorAll('.product-card.' + OPEN).forEach(close);
    sync(c); // état frais avant affichage
    c.classList.add(OPEN);
    setExpanded(c, true);
  }

  function close(c) {
    if (!c) return;
    cancelClose(c);
    c.classList.remove(OPEN);
    setExpanded(c, false);
  }

  function scheduleClose(c) {
    cancelClose(c);
    timers.set(c, setTimeout(function () {
      close(c);
    }, CLOSE_DELAY));
  }

  // Clic sur une pastille du volet → clic natif sur la pastille d'origine.
  // Les gestionnaires directs jQuery de wpbingo réagissent aux événements
  // natifs qui remontent, même si l'élément d'origine est masqué (display:none).
  function select(c, item) {
    if (!c || item.classList.contains('is-disabled')) return;
    var value = item.getAttribute('data-variant');
    var target = null;
    c.querySelectorAll('.wpb-variants-swatch .swatch-content.metal-swatch .swatch-items label[data-variant]').forEach(function (lb) {
      // Comparaison via getAttribute (accents/espaces dans les valeurs).
      if (!target && lb.getAttribute('data-variant') === value) target = lb.closest('.swatch-items');
    });
    if (target && !target.classList.contains('active')) {
      // Le clic relayé remonte jusqu'à document : le drapeau évite que la
      // branche « clic hors zone » ne referme le volet pendant la sélection.
      proxying = true;
      try {
        target.click();
      } finally {
        proxying = false;
      }
    }
    sync(c); // la partie synchrone de wpbingo (classes active) est déjà appliquée
  }

  // --- Souris : ouverture au survol du chevron, maintien sur le volet ---
  // pointerover/pointerout (délégables) + garde pointerType : l'émulation
  // hover du tactile passe exclusivement par le chemin « click ».
  document.addEventListener('pointerover', function (e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    if (!e.target.closest) return;
    var t = e.target.closest('.metals-toggle');
    if (t) {
      open(card(t));
      return;
    }
    var d = e.target.closest('[data-metals-drawer]');
    if (d) cancelClose(card(d));
  });

  document.addEventListener('pointerout', function (e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    if (!e.target.closest) return;
    var z = e.target.closest('.metals-toggle, [data-metals-drawer]');
    if (!z) return;
    var c = card(z);
    if (!c || !c.classList.contains(OPEN)) return;
    if (inZone(e.relatedTarget, c)) return; // déplacement interne à la zone
    scheduleClose(c);
  });

  // --- Tactile / clavier : bascule au clic, sélection, fermeture hors zone ---
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var t = e.target.closest('.metals-toggle');
    if (t) {
      var c = card(t);
      if (c) c.classList.contains(OPEN) ? close(c) : open(c);
      return;
    }
    var item = e.target.closest('.metals-drawer__item');
    if (item) {
      select(card(item), item);
      return;
    }
    if (proxying) return; // clic relayé volet → rangée : ne pas refermer
    // Clic direct sur une pastille de la rangée : re-synchronise le volet.
    var rowItem = e.target.closest('.wpb-variants-swatch .swatch-content.metal-swatch .swatch-items');
    if (rowItem) {
      var rc = card(rowItem);
      if (rc) sync(rc);
      return;
    }
    var openCard = document.querySelector('.product-card.' + OPEN);
    if (openCard && !inZone(e.target, openCard)) close(openCard);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var c = document.querySelector('.product-card.' + OPEN);
    if (!c) return;
    var wasInside = inZone(document.activeElement, c);
    close(c);
    if (wasInside) {
      var t = rowToggleOf(c);
      if (t) t.focus();
    }
  });

  // Fermeture quand le focus clavier sort de la zone chevron + volet.
  document.addEventListener('focusout', function (e) {
    if (!e.target.closest) return;
    var z = e.target.closest('.metals-toggle, [data-metals-drawer]');
    if (!z) return;
    var c = card(z);
    if (c && c.classList.contains(OPEN) && !inZone(e.relatedTarget, c)) scheduleClose(c);
  });

  // --- Infobulles : recalage aux bords « durs » ----------------------------
  // Les bulles (nom du métal) sont centrées sur leur pastille, laquelle est
  // collée au bord de la carte : elles en sortent forcément. La carte les laisse
  // passer (elle ne clippe plus qu'à la verticale), mais deux boîtes clippent
  // encore : le .slick-list des carrousels — qui porte un transform, ce qui
  // interdit toute échappée en position:fixed — et le bord de la page, qu'une
  // bulle de dernière colonne dépasserait en ajoutant une barre de défilement.
  // On mesure donc la bulle contre cette boîte et on écrit le décalage
  // horizontal minimal dans --metal-tip-shift ; le CSS s'en sert pour le
  // translateX et pour recaler la flèche en sens inverse.

  // Le <span> nu du label est l'infobulle ; son frère .metal-mark porte 14K/18K.
  function tipOf(anchor) {
    if (anchor.classList.contains('metals-drawer__item')) {
      return anchor.querySelector('.metals-drawer__tooltip');
    }
    var kids = anchor.children;
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].tagName === 'SPAN' && !kids[i].classList.contains('metal-mark')) return kids[i];
    }
    return null;
  }

  // Boîte de référence : le carrousel s'il y en a un, sinon le conteneur de page.
  function boundsOf(c) {
    var box = c.closest('.slick-list') || c.closest('.container');
    if (box) return box.getBoundingClientRect();
    return { left: 0, right: document.documentElement.clientWidth };
  }

  // On recale TOUTES les bulles de la carte, pas seulement celle survolée : au
  // repos elles sont en visibility:hidden, donc toujours dans le flux, et la
  // carte survolée ne les clippe plus horizontalement. Une seule bulle mal
  // placée suffirait à rallonger la page.
  // Écritures groupées puis lectures groupées puis écritures groupées : un seul
  // recalcul de mise en page pour toute la carte.
  function placeTips(c) {
    var tips = [];
    c.querySelectorAll(TIP_ANCHORS).forEach(function (a) {
      var tip = tipOf(a);
      if (!tip) return;
      // Repartir de la position centrée : la mesure doit porter sur la géométrie
      // naturelle, et cela purge une valeur héritée d'un clone slick ou d'un
      // survol précédent.
      tip.style.removeProperty('--metal-tip-shift');
      tips.push(tip);
    });
    if (!tips.length) return;
    var b = boundsOf(c);
    var shifts = tips.map(function (tip) {
      var t = tip.getBoundingClientRect();
      if (!t.width) return 0; // bulle en display:none (< 768px) : rect à zéro
      var over = b.left + TIP_MARGIN - t.left;
      if (over > 0) return Math.round(over); // à gauche : on garde le début du texte
      over = t.right - (b.right - TIP_MARGIN);
      return over > 0 ? -Math.round(over) : 0;
    });
    tips.forEach(function (tip, i) {
      if (shifts[i]) tip.style.setProperty('--metal-tip-shift', shifts[i] + 'px');
    });
  }

  // À l'entrée du pointeur dans une carte. Volontairement SANS garde
  // pointerType : celle des écouteurs du volet ci-dessus n'existe que parce que
  // le volet est piloté au clic sur tactile. Les bulles, elles, sont affichées
  // par :hover CSS — donc aussi par le survol rémanent d'un tap, et celle du
  // volet n'est pas masquée en mobile.
  document.addEventListener('pointerover', function (e) {
    if (!e.target.closest) return;
    var c = e.target.closest('.product-card');
    if (!c) return;
    if (e.relatedTarget && c.contains(e.relatedTarget)) return; // déplacement interne
    placeTips(c);
  });
})();
